import React, { createContext, useContext, useMemo, useRef, useState } from 'react';
import { showBrowserSystemNotification } from '../services/webPush/firebaseWebPush';
import { resolveNotificationTarget } from '../utils/notificationDeepLink';

const NotificationContext = createContext();

export function NotificationProvider({ children }) {
  const [unreadCount, setUnreadCount] = useState(0);
  const initializedRef = useRef(false);
  const seenNotificationIdsRef = useRef(new Set());

  const updateUnreadCount = (count) => {
    setUnreadCount(count);
  };

  const decrementUnreadCount = () => {
    setUnreadCount(prev => Math.max(0, prev - 1));
  };

  const resetUnreadCount = () => {
    setUnreadCount(0);
  };

  const syncNotifications = (notifications = [], options = {}) => {
    const safeNotifications = Array.isArray(notifications) ? notifications : [];
    const unreadNotifications = safeNotifications.filter((notification) => Number(notification?.is_read) === 0);
    setUnreadCount(unreadNotifications.length);

    const currentIds = new Set(
      safeNotifications
        .map((notification) => Number(notification?.notificationId || notification?.id))
        .filter(Boolean)
    );

    if (initializedRef.current && options.showPopups) {
      unreadNotifications.forEach((notification) => {
        const notificationId = Number(notification?.notificationId || notification?.id);

        if (!notificationId || seenNotificationIdsRef.current.has(notificationId)) {
          return;
        }

        seenNotificationIdsRef.current.add(notificationId);
        const target =
          resolveNotificationTarget(notification, localStorage.getItem('role')) ||
          notification?.deepLink ||
          '/user/notifications';
        showBrowserSystemNotification({
          title: notification?.title || 'Notification',
          body: notification?.body || '',
          deepLink: target,
          tag: `inbox-${notificationId}`,
        }).catch((error) => {
          console.warn('[NotificationContext] Failed to display browser notification:', error);
        });
      });
    } else {
      currentIds.forEach((id) => seenNotificationIdsRef.current.add(id));
    }

    initializedRef.current = true;
  };

  const value = useMemo(() => ({
    unreadCount,
    updateUnreadCount,
    decrementUnreadCount,
    resetUnreadCount,
    syncNotifications,
  }), [unreadCount]);

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
}

export function useNotification() {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotification must be used within a NotificationProvider');
  }
  return context;
} 
